<template>
  <v-card>
    <v-card-title class="flex-nowrap">
      <div class="flex-grow-1 flex-shrink-1 pr-4">
        {{ $t(`stepModelRequirement.${requirement.requirementId}.name`) }}
      </div>

      <WipSwitch :requirement="requirement" model="step" class="mr-2" />

      <WipAuditorSwitch :requirement="requirement" model="step" />

      <div>
        <AuditRequirementInfo :requirement="requirement" />
      </div>

      <div class="pl-4">
        <v-btn v-if="!edit" @click="edit = true" color="secondary" icon>
          <v-icon>mdi-pencil</v-icon>
        </v-btn>

        <v-btn v-if="edit" @click="edit = false" icon>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text v-if="edit">
      <v-row>
        <v-col cols="4">
          <v-row>
            <v-col cols="12">
              <v-card outlined>
                <v-card-title>
                  {{ $t("auditor.stepModelRequirementForm.description") }}
                </v-card-title>

                <v-card-text>
                  <Markdown :value="$t(`stepModelRequirement.${requirement.requirementId}.description`)"></Markdown>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="$t(`stepModelRequirement.${requirement.requirementId}.help`).trim().length > 0">
              <v-card outlined>
                <v-card-title>
                  {{
                    fund.auditPeriod.year > 2024
                      ? $t("auditor.stepModelRequirementForm.help2025")
                      : $t("auditor.stepModelRequirementForm.help")
                  }}
                </v-card-title>

                <v-card-text>
                  <Markdown :value="$t(`stepModelRequirement.${requirement.requirementId}.help`)"></Markdown>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="attachments.length > 0">
              <v-dialog v-model="attachmentsDialog" persistent max-width="600">
                <template #activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" color="secondary">
                    {{ $t("action.downloadAll", { count: attachments.length }) }}
                  </v-btn>
                </template>

                <v-card>
                  <v-card-title class="headline">
                    {{ $t("auditor.download.confirmation.title") }}
                  </v-card-title>

                  <v-card-text>
                    <Markdown :value="$t('auditor.download.confirmation.description')" />
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn color="warning" @click="attachmentsDialog = false">
                      {{ $t("auditor.download.confirmation.cancel") }}
                    </v-btn>

                    <v-btn
                      color="primary"
                      @click="
                        downloadAllAttachments();
                        attachmentsDialog = false;
                      "
                    >
                      {{ $t("auditor.download.confirmation.accept") }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="8">
          <v-row>
            <v-col cols="12" v-if="requirement.userFeedback">
              <v-card outlined>
                <v-card-title>{{ $t("userFeedback.title") }}</v-card-title>

                <v-card-text>
                  <Markdown :value="requirement.userFeedback" />
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="requirement.userFeedback">
              <StepModelRequirementAuditorFeedbackForm :requirement="requirement" />
            </v-col>

            <v-col cols="12">
              <ReferenceInfo :fund="fund" model="step" :requirement="requirement" audit />
            </v-col>

            <v-col v-if="displayResult" cols="12">
              <RequirementResultInfo :requirement="requirement" />
            </v-col>

            <v-col cols="12">
              <ResponseList :responses="requirement.protocol" />
            </v-col>

            <v-col cols="12" v-if="requirement.referenced && requirement.pendingResponse">
              {{ $t("pendingResponse.title") }}
              <ResponseList :responses="[requirement.pendingResponse]" />
            </v-col>

            <v-col cols="12" v-if="displayButtons">
              <v-btn color="primary" class="mr-4" @click="showResponseForm" v-if="displayAddResponseButton">
                {{ $t("auditor.stepModelRequirementForm.addResponse") }}
              </v-btn>

              <v-btn color="primary" class="mr-4" @click="showResultForm">
                {{ $t("auditor.stepModelRequirementForm.addResult") }}
              </v-btn>
            </v-col>

            <v-col cols="12" v-if="displayResponseForm">
              <RequirementResponseForm type="auditor" model="step" :requirement="requirement" />
            </v-col>

            <v-col cols="12" v-if="displayResultForm">
              <StepModelRequirementResultForm :requirement="requirement" :fund="fund" @save="resultForm = false" />
            </v-col>

            <v-col cols="12">
              <v-card outlined>
                <v-card-title>
                  {{ $t("auditor.stepModelRequirementForm.implementation") }}
                </v-card-title>

                <v-card-text>
                  <Markdown :value="$t(`stepModelRequirement.${requirement.requirementId}.implementation`)"></Markdown>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" v-if="!requirement.referenced">
              <InternalCommentForm model="step" :requirement="requirement" />
            </v-col>

            <v-col cols="12" v-if="requirement.referenced">
              <v-card outlined>
                <v-card-title>
                  {{ $t("auditor.internalCommentForm.title") }}
                </v-card-title>

                <v-card-text>
                  {{ requirement.InternalComment }}
                </v-card-text>

                <v-card-title>
                  {{ $t("auditor.notReferencedInternalCommentForm.title") }}
                </v-card-title>

                <v-card-text>
                  {{ requirement.referenceRequirement.notReferencedInternalComment }}
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12">
              <NotReferencedInternalCommentForm model="step" :requirement="requirement" />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { saveAs } from "file-saver";
import { includes, pluck, flatten } from "ramda";
import ResponseList from "@/components/Fund/ResponseList";
import AuditRequirementInfo from "./AuditRequirementInfo";
import WipSwitch from "@/components/Requirement/WipSwitch.vue";
import WipAuditorSwitch from "@/components/Requirement/WipAuditorSwitch.vue";
import ReferenceInfo from "@/components/Requirement/ReferenceInfo";
import StepModelRequirementResultForm from "./StepModelRequirementResultForm";
import InternalCommentForm from "@/components/Requirement/InternalCommentForm";
import NotReferencedInternalCommentForm from "@/components/Requirement/NotReferencedInternalCommentForm";
import RequirementResultInfo from "@/components/Requirement/RequirementResultInfo";
import RequirementResponseForm from "@/components/Requirement/RequirementResponseForm";
import StepModelRequirementAuditorFeedbackForm from "./StepModelRequirementAuditorFeedbackForm.vue";

export default {
  components: {
    WipSwitch,
    WipAuditorSwitch,
    ResponseList,
    ReferenceInfo,
    InternalCommentForm,
    AuditRequirementInfo,
    RequirementResultInfo,
    RequirementResponseForm,
    StepModelRequirementResultForm,
    NotReferencedInternalCommentForm,
    StepModelRequirementAuditorFeedbackForm,
  },

  props: {
    requirement: {
      type: Object,
      required: true,
    },

    fund: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      edit: false,
      resultForm: false,
      responseForm: false,
      attachmentsDialog: false,
    };
  },

  computed: {
    displayResult() {
      if (this.requirement.state !== "DONE") return false;
      return true;
    },

    displayButtons() {
      if (this.requirement.referenced) return false;
      if (this.fund.stepModelRequirementsState === "FINAL_AUDIT") return false;
      return !includes(this.requirement.state, ["DONE", "EDIT"]);
    },

    displayResponseForm() {
      if (this.requirement.referenced) return false;
      if (this.fund.stepModelRequirementsState === "FINAL_AUDIT") return false;
      if (this.requirement.state === "EDIT") return true;
      if (this.requirement.state === "AUDIT" && this.responseForm) return true;
      return false;
    },

    displayResultForm() {
      if (this.requirement.referenced) return false;
      if (this.requirement.state === "DONE") return true;
      if (this.requirement.state === "AUDIT" && this.resultForm) return true;
      if (this.fund.stepModelRequirementsState === "FINAL_AUDIT") return true;
      return false;
    },

    displayAddResponseButton() {
      return includes(this.fund.stepModelRequirementsState, ["AUDIT", "SECOND_AUDIT", "INTERVIEW_AUDIT"]);
    },

    attachments() {
      return flatten(pluck("attachments", this.requirement.protocol));
    },
  },

  methods: {
    showResponseForm() {
      this.responseForm = true;
      this.resultForm = false;
    },

    showResultForm() {
      this.responseForm = false;
      this.resultForm = true;
    },

    downloadAllAttachments() {
      this.download(this.attachments);
    },

    download(attachments) {
      attachments.forEach((attachment, index) => {
        setTimeout(
          () =>
            fetch(attachment.file.url)
              .then((res) => res.blob())
              .then((blob) => {
                saveAs(blob, attachment.file.name);
              }),
          index * 500
        );
      });
    },
  },
};
</script>
